import React, { useState, useEffect, useRef } from "react";
import { useList } from "@/lib/hooks";
import { PageHeader, Panel, EmptyState, money } from "@/components/common";
import { Trophy, Medal, TrendUp, Target } from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const RANK_STYLE = {
  1: { ring: "ring-amber-300", bg: "bg-amber-50", text: "text-amber-600", label: "1st" },
  2: { ring: "ring-slate-300", bg: "bg-slate-50", text: "text-slate-500", label: "2nd" },
  3: { ring: "ring-orange-200", bg: "bg-orange-50", text: "text-orange-500", label: "3rd" },
};
const PERIODS = [
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "all", label: "All Time" },
];

export default function Leaderboard() {
  const [period, setPeriod] = useState("month");
  const { data, isLoading } = useList(["leaderboard", period], `/sales/leaderboard?period=${period}`, { refetchInterval: 30000 });
  const rows = data?.rows || [];
  const team = data?.team || { target: 0, achieved: 0, progress: 0 };
  const top = rows.slice(0, 3);
  const celebrated = useRef(false);

  useEffect(() => {
    if (team.target > 0 && team.progress >= 100 && !celebrated.current) {
      celebrated.current = true;
      const fire = (ratio, opts) => confetti({ particleCount: Math.floor(200 * ratio), spread: 70, origin: { y: 0.6 }, colors: ["#2563eb", "#f59e0b", "#10b981", "#ffffff"], ...opts });
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.35, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      setTimeout(() => fire(0.2, { spread: 120, startVelocity: 45 }), 250);
      toast.success(`🎉 Team goal smashed! ${team.progress}% of ${money(team.target)} target reached.`, { duration: 6000 });
    }
    if (team.progress < 100) celebrated.current = false;
  }, [team.target, team.progress]);

  return (
    <div>
      <PageHeader title="Sales Leaderboard" subtitle="Ranked by commission earned and conversions — may the best closer win" />

      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-6" data-testid="period-toggle">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${period === p.key ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {team.target > 0 && (
        <Panel className="p-5 mb-6" data-testid="team-goal">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Target size={18} className="text-blue-600" />Team Goal</h3>
            <span className="text-sm text-slate-500">{money(team.achieved)} / {money(team.target)}</span>
          </div>
          <div className="h-4 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${Math.min(100, team.progress)}%` }}>
              {team.progress >= 12 && <span className="text-[10px] font-bold text-white">{team.progress}%</span>}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{team.progress}% of the combined team target achieved this {period === "all" ? "period" : period}</p>
        </Panel>
      )}

      {isLoading ? (
        <div className="text-slate-400">Loading leaderboard…</div>
      ) : rows.length === 0 ? (
        <Panel><EmptyState icon={Trophy} title="No sales agents yet" hint="Add sales agents and convert leads to populate the board." /></Panel>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {top.map((r) => {
              const s = RANK_STYLE[r.rank] || RANK_STYLE[3];
              return (
                <Panel key={r.id} className={`p-5 text-center ring-2 ${s.ring}`} data-testid={`podium-${r.rank}`}>
                  <div className={`w-12 h-12 rounded-full ${s.bg} grid place-items-center mx-auto mb-2`}>
                    {r.rank === 1 ? <Trophy size={24} weight="fill" className={s.text} /> : <Medal size={24} weight="fill" className={s.text} />}
                  </div>
                  <p className={`text-xs font-bold uppercase tracking-wide ${s.text}`}>{s.label}</p>
                  <p className="font-heading font-extrabold text-slate-900 mt-1 truncate">{r.name}</p>
                  <p className="font-heading font-bold text-2xl text-blue-600 mt-2">{money(r.commission)}</p>
                  <p className="text-xs text-slate-500">{r.converted} deals · {r.win_rate}% win</p>
                </Panel>
              );
            })}
          </div>

          <Panel className="p-0 overflow-hidden" data-testid="leaderboard-table">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <TrendUp size={18} className="text-blue-600" /><h3 className="font-heading font-bold text-slate-900">Full Standings</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((r) => {
                const progress = r.target > 0 ? Math.min(100, Math.round((r.achieved / r.target) * 100)) : 0;
                return (
                  <div key={r.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50" data-testid={`leader-row-${r.id}`}>
                    <span className={`w-7 text-center font-heading font-bold ${r.rank <= 3 ? "text-amber-500" : "text-slate-400"}`}>{r.rank}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{r.name}</p>
                      <p className="text-xs text-slate-400">{r.converted} converted · {r.win_rate}% win rate</p>
                      {r.target > 0 && (
                        <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-[220px]">
                          <div className="h-full bg-blue-600 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-semibold text-blue-600">{money(r.commission)}</p>
                      <p className="text-xs text-slate-400">{money(r.achieved)} earned</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
